import { inject } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import type { CanActivateChildFn } from '@angular/router';
import { Router } from '@angular/router';
import { firstInvalidStep } from '../misc/first-invalid-step';

const STEP_PATH_RE = /^step(\d+)$/;

/**
 * Blocks deep-linking past an incomplete step and redirects to the first one that blocks the way.
 *
 * A factory rather than a guard, because the forms live in each Sparte app and `libs/*` must never
 * name a Sparte type. `getAllFgs` is called inside the guard, so it may use `inject()`:
 *
 * ```ts
 * export const stepAccessGuard = createStepAccessGuard(
 *   KfzRoutes.formular,
 *   () => inject(FormsService).allFgs
 * );
 * ```
 */
export const createStepAccessGuard =
  (formularUrl: string, getAllFgs: () => Record<string, AbstractControl>): CanActivateChildFn =>
  route => {
    const path = route.url[0]?.path;
    const match = path?.match(STEP_PATH_RE);
    if (!match) return true;

    const target = Number(match[1]);
    if (target <= 1) return true;

    const router = inject(Router);
    const blocking = firstInvalidStep(getAllFgs(), target);

    return blocking ? router.createUrlTree([formularUrl, blocking]) : true;
  };
